import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { ToastController, ModalController, LoadingController, NavParams } from '@ionic/angular';
import { DashboardService } from 'src/app/dashboard/dashboard.service';
import { ExternalEvaluationsService } from 'src/app/external-evaluation/external-evaluations.service';
import { ExternalEvaluationStatus, ExternalEvaluationType } from 'src/app/external-evaluation/_interfaces/external-evaluation'
import { ExternalUserConfig } from 'src/app/external-evaluation/_interfaces/external-user-config';
import { BaseClass } from '../../classes/base';
import { BehaviorSubject } from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import { TranslationService } from '../../services/translation.service';
import { UsersService } from 'src/app/users/users.service';
import { SubjectCycleParticipant } from 'src/app/external-evaluation/_interfaces/subject-cycle-participant';
import { CyclePreview } from '../../interfaces/cycle.interface';

@Component({
  selector: 'app-external-user-creation',
  templateUrl: './external-user-creation.component.html',
  styleUrls: ['./external-user-creation.component.scss']
})
export class ExternalUserCreationComponent extends BaseClass implements OnInit, OnDestroy {
  public users: any[] = [];
  public evaluatedUser: SubjectCycleParticipant = null;

  public formGroup: UntypedFormGroup;
  public isAdmin: boolean;
  public cycles: Array<CyclePreview> = [];
  public selectedCycleId = null;
  public types = [
    ExternalEvaluationType.FreeEvaluationEndYear,
    ExternalEvaluationType.FreeEvaluationMidYear,
    ExternalEvaluationType.ThreeSixtyEvaluation,
    ExternalEvaluationType.ThreeSixtyEvaluationGroupCompanies

  ];

  private _searchSubject: BehaviorSubject<string> = new BehaviorSubject('');
  private _externalUserId: string;

  constructor(
    protected _toastController: ToastController,
    protected _translationService: TranslationService,
    private _modalController: ModalController,
    private _loadingController: LoadingController,
    private _evaluationService: ExternalEvaluationsService,
    private _dashboardService: DashboardService,
    private _navParams: NavParams,
    private _userService: UsersService
  ) {
    super(_toastController, _translationService);
    const externalUser = this._navParams.get('externalUser') as ExternalUserConfig;
    this.isAdmin = this._navParams.get('isAdmin');
    this.evaluatedUser = externalUser !== null ?  {
      name: externalUser.evaluatedUserName,
      id: externalUser.evaluatedUserId,
      imageUrl: externalUser.evaluatedUserImageUrl
    } : null;

    this._externalUserId = externalUser ? externalUser.id : null;
    this.formGroup = this._createGoalFormGroup(externalUser);
  }

  ngOnInit() {
    this._loadCycles();
    this._setSubscriptionSearch();
  }

  ngOnDestroy(): void {
    this._searchSubject.unsubscribe();
  }

  public setEvaluatedUser(user: any) {
    this.evaluatedUser = { name: user.name, id: user.userId, imageUrl: user.imageUrl };
    this.users = [];
  }

  public removeEvaluatedUser() {
    this.evaluatedUser = null;
  }

  public getTypeText(type: ExternalEvaluationType){
    switch (type) {
      case ExternalEvaluationType.FreeEvaluationMidYear:
        return 'performanceCycle.freeEvalMidYear';
      case ExternalEvaluationType.FreeEvaluationEndYear:
        return 'performanceCycle.freeEvalEndYear';
      case ExternalEvaluationType.ThreeSixtyEvaluation:
        return 'performanceCycle.threeSixtyEvaluation';
      case ExternalEvaluationType.ThreeSixtyEvaluationGroupCompanies:
        return 'performanceCycle.threeSixtyEvaluationGroupCompanies';
    }
  }

  public onChangeSearch(value: string) {
    this._searchSubject.next(value);
  }

  public dismiss(reload: boolean = false) {
    this._modalController.dismiss( reload );
  }

  public save(): void {
    this._externalUserId ?
      this.updateExternalUser() :
      this.addExternalUser();
  }

  public async addExternalUser(): Promise<void> {

    if (this.formGroup.valid && this.evaluatedUser !== null) {

      const loading = await this._loadingController.create();
      await loading.present();

      const externalUser = this.formGroup.getRawValue() as ExternalUserConfig;
      externalUser.evaluatedUserId = this.evaluatedUser.id;

      this._evaluationService.addExternalUser(externalUser).subscribe(() => {
        loading.dismiss();
        this.notify('Usuario externo adicionado com sucesso', 'success');
        this.dismiss(true);

      }, (error) => {
        loading.dismiss();
        this.notify( this.getErrorNotification(error), 'danger' );
      });
    } else {
      this.notify(
        this._translationService.getTranslation(
          'generic.missingFields'
        ), 'danger'
      );
    }
  }

  public async updateExternalUser(): Promise<void> {
    if (this.formGroup.valid) {
      const externalUser = this.formGroup.getRawValue() as ExternalUserConfig;

      if (externalUser.status === ExternalEvaluationStatus.Sent && !this.isAdmin) {
        this.notify(
          this._translationService.getTranslation(
            'generic.generic.processAlreadyClosed'
          ), 'danger'
        );
      }

      externalUser.id = this._externalUserId;
      externalUser.evaluatedUserId = this.evaluatedUser.id;

      const loading = await this._loadingController.create();
      await loading.present();
      this._evaluationService.updateExternalUser(externalUser).subscribe(() => {
        loading.dismiss();
        this.notify('Usuario externo atualizado com sucesso', 'success');
        this.dismiss(true);

      }, (error) => {
        loading.dismiss();
        this.notify( this.getErrorNotification(error), 'danger' );
      });

    } else {
      this.notify(
        this._translationService.getTranslation(
          'generic.missingFields'
        ), 'danger'
      );
    }
  }

  private _setSubscriptionSearch() {
    this._searchSubject.pipe(
        distinctUntilChanged(),
        debounceTime(1000)
    ).subscribe(data => {
      if (data) {
        this._loadUsers(data);
      } else {
        this.users = [];
      }
    });
  }

  private async _loadCycles(): Promise<void> {
    this._dashboardService.getUserCycles().subscribe((response) => {
      this.cycles = response.data;

      if (this.cycles && this.cycles.length > 0) {
        this.selectedCycleId = this.cycles[0].cycleId;
        const cycleId = this.formGroup.get('cycleId').value;
        this.formGroup.patchValue({ cycleId: cycleId ? cycleId : this.selectedCycleId });
      }
    });
  }

  private async _loadUsers(searchValue: string = '') {
    const loading = await this._loadingController.create();
    await loading.present();

    this._userService.getUsers(1, 5, searchValue)
    .subscribe((response) => {
      this.users = response.data.users;
      loading.dismiss();
    }, (error) => {
      loading.dismiss();
      this.notifyError( error );
    });
  }

  private _createGoalFormGroup(externalUser: ExternalUserConfig = null): UntypedFormGroup {
    const formGroup = new UntypedFormGroup({
      'name': new UntypedFormControl(
        externalUser ? externalUser.name : '',
        [ Validators.required ]
      ),
      'email': new UntypedFormControl(
        externalUser ? externalUser.email : '',
        [ Validators.required ]
      ),
      'role': new UntypedFormControl(
        externalUser ? externalUser.role : '',
        [ Validators.required ]
      ),
      'company': new UntypedFormControl(
        externalUser ? externalUser.company : '',
        [ Validators.required ]
      ),
      'type': new UntypedFormControl(
        externalUser ? externalUser.type : '',
        [ Validators.required ]
      ),
      'cycleId': new UntypedFormControl(
        externalUser ? externalUser.cycleId : this.selectedCycleId,
        [ Validators.required ]
      )
    });

    return formGroup;
  }
}
